// src/components/ReportsTab.js
// Vendor "Daily Collection Report" — online vs cash breakdown + CSV export.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import DateRangePicker from './DateRangePicker';
import { downloadCSV } from '../utils/downloadCSV';

function toDateStr(d) { return d.toISOString().slice(0, 10); }

function defaultRange() {
  const end = new Date();
  const start = new Date(end); start.setDate(start.getDate() - 29);
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}

const cardStyle = { background: 'var(--bg2)', borderRadius: 10, padding: 14, minWidth: 140 };

export default function ReportsTab() {
  const [range, setRange]   = useState(defaultRange());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  const load = useCallback(async (r) => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/vendors/me/reports/daily', { params: r });
      setReport(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(range); }, [load, range]);

  const totals = report?.totals;

  return (
    <div className="card">
      <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>📊 Daily Collection Report</div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>
        Online vs cash payment breakdown for your sales.
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <DateRangePicker startDate={range.startDate} endDate={range.endDate} onChange={setRange} />
        <button
          className="btn btn-sm"
          onClick={() => downloadCSV('/vendors/me/reports/daily/csv', range, `daily-report-${range.startDate}-to-${range.endDate}.csv`)}
        >
          📥 CSV
        </button>
      </div>

      {loading && <div style={{ color: 'var(--text2)' }}>Loading…</div>}
      {error && <div style={{ color: '#e55' }}>{error}</div>}

      {totals && !loading && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Total</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.combined.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.combined.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Online</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.online.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.online.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Cash</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.cash.total.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>{totals.cash.orders} orders</div>
            </div>
            <div style={cardStyle}>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>Net Payable</div>
              <div style={{ fontSize: 18, fontWeight: 700 }}>AED {totals.totalNetPayable.toFixed(2)}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)' }}>after AED {totals.totalCommission.toFixed(2)} commission</div>
            </div>
          </div>

          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>Daily Breakdown</div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Online Ord</th><th>Online Amt</th>
                  <th>Cash Ord</th><th>Cash Amt</th>
                  <th>Total</th><th>Commission</th><th>Net</th>
                </tr>
              </thead>
              <tbody>
                {byDate(report.dailyBreakdown).map(d => (
                  <tr key={d.date}>
                    <td>{d.date}</td>
                    <td>{d.online.orders}</td><td>{d.online.total.toFixed(2)}</td>
                    <td>{d.cash.orders}</td><td>{d.cash.total.toFixed(2)}</td>
                    <td>{d.combined.total.toFixed(2)}</td>
                    <td>{d.commission.toFixed(2)}</td>
                    <td>{d.netPayable.toFixed(2)}</td>
                  </tr>
                ))}
                {report.dailyBreakdown.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text2)' }}>No paid orders in this period</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// dailyBreakdown is per-day-per-vendor; for a single vendor it's effectively per-day already,
// but aggregate defensively in case of multiple rows for the same date.
function byDate(rows) {
  const map = {};
  for (const r of rows) {
    if (!map[r.date]) {
      map[r.date] = {
        date: r.date,
        online: { orders: 0, total: 0 }, cash: { orders: 0, total: 0 }, combined: { orders: 0, total: 0 },
        commission: 0, netPayable: 0,
      };
    }
    const d = map[r.date];
    d.online.orders += r.online.orders; d.online.total += r.online.total;
    d.cash.orders   += r.cash.orders;   d.cash.total   += r.cash.total;
    d.combined.orders += r.combined.orders; d.combined.total += r.combined.total;
    d.commission += r.commission;
    d.netPayable += r.netPayable;
  }
  return Object.values(map).sort((a, b) => b.date.localeCompare(a.date));
}
